import React, { Fragment, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useGetUserById, useSendResetPasswordEmail, useUpdateUserStatus, useUpdateUser } from "../../custom-hooks/use-users";
import moment from "moment";
import { Breadcrumbs, Button, Spinner, Switch } from "@material-tailwind/react";
import { convertToPresentableText, Notify, NotifyError } from "../../utils/functions";
import {
    Building2Icon,
    ClockIcon,
    CopyIcon,
    HomeIcon,
    IdCardIcon,
    MailIcon,
    PhoneIcon,
    UserIcon,
    ArrowLeftIcon,
    UsersIcon,
    BriefcaseIcon,
    CalendarIcon,
    ShieldIcon,
    KeyIcon,
    AlertCircleIcon,
    Settings2Icon,
    LogInIcon
} from "lucide-react";
import { useConfirm } from "../../custom-hooks/confirm-dialog";
import { BottomUXSpace } from "../../components";
import { URLS } from "../../router-x/routes";
import { usePermissions } from "../../custom-hooks/use-permissions";
import { useAuth } from "../../services/contexts/authContext";
import { STATIC_USER_ROLES } from "../../utils/enums";

const TeamMemberDetailsPage = () => {
    const { can } = usePermissions();
    const { customerId, teamMemberId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { confirm, ConfirmationDialog } = useConfirm();
    const { userDetails, startImpersonation } = useAuth();
    const [impersonating, setImpersonating] = useState(false);

    const {
        data: customerDetails,
        isLoading: isCustomerDetailsLoading,
        error: customerDetailsError,
    } = useGetUserById(customerId);

    const {
        data: teamMemberDetails,
        isLoading: isTeamMemberDetailsLoading,
        error: teamMemberDetailsError,
        refetch: refetchTeamMemberDetails,
    } = useGetUserById(teamMemberId);

    const customer = useMemo(() => customerDetails?.data?.user || {}, [customerDetails?.data?.user]);
    const teamMember = useMemo(() => teamMemberDetails?.data?.user || {}, [teamMemberDetails?.data?.user]);

    const updateUserStatusMutation = useUpdateUserStatus();
    const sendResetPasswordEmailMutation = useSendResetPasswordEmail();
    const updateUserMutation = useUpdateUser();

    // Same gate the customer detail page uses. The backend's startImpersonation is
    // role-agnostic (it only resolves targetUserId), so a team member works as a target
    // exactly like a customer does — only the post-switch landing route differs.
    const canImpersonate = userDetails?.role === STATIC_USER_ROLES.SUPER_ADMIN ||
        can('FX12-1', 'impersonate');

    const handleImpersonate = async () => {
        try {
            setImpersonating(true);
            // Pass current path so the admin is returned here after stopping.
            await startImpersonation(teamMemberId, location.pathname);
            navigate(URLS.TEAM_MEMBER.HOME);
        } catch (err) {
            console.error('Failed to start impersonation:', err);
            setImpersonating(false);
        }
    };


    if (isCustomerDetailsLoading || isTeamMemberDetailsLoading) {
        return (
            <div className="flex justify-center p-4 m-4 items-center h-screen border rounded-3xl">
                <Spinner />
            </div>
        );
    }

    if (customerDetailsError || teamMemberDetailsError) {
        return (
            <Fragment>
                <div className="p-4 flex flex-col justify-center items-center h-96">
                    <div className="p-4 text-red-600 text-center">
                        <AlertCircleIcon size={48} className="mx-auto mb-4 text-red-400" />
                        <h2 className="text-xl font-bold">Error Loading Data</h2>
                        <p>{(customerDetailsError || teamMemberDetailsError)?.message || "Failed to load details"}</p>
                        <Button
                            className="rounded-full mt-4"
                            variant="gradient"
                            color="gray"
                            onClick={() => navigate(`${URLS.ADMIN.CUSTOMER}/${customerId}`)}
                        >
                            Go Back to Customer Details
                        </Button>
                    </div>
                </div>
            </Fragment>
        );
    }

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        Notify("Copied to clipboard", "success");
    };


    const handleUpdateUserStatus = async () => {
        const confirmed = await confirm({
            title: `${teamMember.isActive ? 'Disable' : 'Enable'} Team Member`,
            message: `Are you sure you want to ${teamMember.isActive ? 'disable' : 'enable'} ${teamMember.name}'s account?`,
            confirmText: teamMember.isActive ? 'Disable' : 'Enable',
            confirmColor: teamMember.isActive ? 'red' : 'green'
        });

        if (!confirmed) return;

        try {
            await updateUserStatusMutation.mutateAsync({
                userId: teamMemberId,
                status: !teamMember.isActive
            });
            refetchTeamMemberDetails();
            Notify(`Team member account ${teamMember.isActive ? 'disabled' : 'enabled'} successfully`, "success");
        } catch (error) {
            console.error("Error updating user status:", error);
            NotifyError("Failed to update account status", error);
        }
    };

    const handleSendResetPasswordEmail = async () => {
        const confirmed = await confirm({
            title: 'Send Password Reset Email',
            message: `Send a password reset email to ${teamMember.email}?`,
            confirmText: 'Send Email',
            confirmColor: 'blue'
        });

        if (!confirmed) return;

        try {
            await sendResetPasswordEmailMutation.mutateAsync({
                email: teamMember.email,
                userId: teamMemberId
            });
            Notify("Password reset email sent successfully", "success");
        } catch (error) {
            console.error("Error sending reset password email:", error);
            NotifyError("Failed to send password reset email", error);
        }
    };

    const handleTogglePermission = async (pageId, currentStatus) => {
        try {
            const currentPages = teamMember.allowedPages || [];
            let newPages = [...currentPages];
            if (currentStatus) {
                newPages = newPages.filter(p => p !== pageId);
            } else {
                newPages.push(pageId);
            }

            await updateUserMutation.mutateAsync({
                userId: teamMemberId,
                data: { allowedPages: newPages }
            });
            refetchTeamMemberDetails();
        } catch (error) {
            console.error("Error updating permissions:", error);
            NotifyError("Failed to update permissions", error);
        }
    };

    return (
        <div className="p-6">
            {/* Header Section */}
            <div className="mb-8">
                <Breadcrumbs className="mb-4">
                    <Link to="/" className="opacity-60 flex items-center gap-1 hover:opacity-100 transition-opacity">
                        <HomeIcon size={14} />
                        <span>Home</span>
                    </Link>
                    <Link to="/customer" className="opacity-60 flex items-center gap-1 hover:opacity-100 transition-opacity">
                        <Building2Icon size={14} />
                        <span>Customers</span>
                    </Link>
                    <Link to={`/customer/${customerId}`} className="opacity-60 flex items-center gap-1 hover:opacity-100 transition-opacity">
                        <UserIcon size={14} />
                        <span>{customer?.name || 'Customer'}</span>
                    </Link>
                    <Link to={`/customer/${customerId}`} className="opacity-60 flex items-center gap-1 hover:opacity-100 transition-opacity">
                        <UsersIcon size={14} />
                        <span>Team Members</span>
                    </Link>
                    <span className="flex items-center gap-1 text-gray-900">
                        <UserIcon size={14} />
                        <span>{teamMember?.name || 'Team Member'}</span>
                    </span>
                </Breadcrumbs>

                <Button
                    variant="text"
                    size="sm"
                    className="flex items-center gap-2 mb-6 hover:bg-gray-100 transition-colors"
                    onClick={() => navigate(`${URLS.ADMIN.CUSTOMER}/${customerId}`)}
                >
                    <ArrowLeftIcon size={16} />
                    Back to Customer Details
                </Button>
            </div>

            {/* Customer Context Banner */}
            <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-8 rounded-r-lg">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Building2Icon size={20} className="text-blue-600" />
                        <div>
                            <h3 className="text-lg font-semibold text-blue-900">{customer.name}</h3>
                            <p className="text-sm text-blue-700">Parent Customer Organization</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${customer.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            {customer.isActive ? 'Active' : 'Inactive'}
                        </span>
                        <Button
                            variant="outlined"
                            size="sm"
                            className="rounded-full border-blue-300 text-blue-600 hover:bg-blue-50"
                            onClick={() => navigate(`${URLS.ADMIN.CUSTOMER}/${customerId}`)}
                        >
                            View Customer Details
                        </Button>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="space-y-8">
                {/* Team Member Profile */}
                <div className="border rounded-3xl p-8">
                    <div className="flex items-start justify-between mb-8">
                        <div className="flex items-center gap-4">
                            <div className="w-20 h-20 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center">
                                <UserIcon size={36} className="text-white" />
                            </div>
                            <div>
                                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                                    {teamMember.name || "Team Member Name"}
                                </h1>
                                <div className="flex items-center gap-2 text-gray-600">
                                    <BriefcaseIcon size={16} />
                                    <span>{teamMember.jobTitle || "No Job Title"}</span>
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            {canImpersonate && teamMember.isActive && (
                                <button
                                    id="impersonate-team-member-btn"
                                    onClick={handleImpersonate}
                                    disabled={impersonating}
                                    className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 hover:border-amber-300 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {impersonating ? <Spinner className="h-3 w-3" /> : <LogInIcon size={14} />}
                                    <span>{impersonating ? 'Switching...' : 'Login as this User'}</span>
                                </button>
                            )}
                            <span className={`px-4 py-2 rounded-full text-sm font-medium ${teamMember.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                {teamMember.isActive ? 'Active' : 'Inactive'}
                            </span>
                            <span className="px-4 py-2 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800">
                                {convertToPresentableText(teamMember.role)}
                            </span>
                        </div>
                    </div>

                    {/* Information Grid */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {/* Contact Information */}
                        <div>
                            <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
                                <MailIcon size={20} className="text-gray-600" />
                                Contact Information
                            </h2>
                            <div className="space-y-4">
                                <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                                    <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                                        <MailIcon size={18} className="text-gray-600" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-sm text-gray-500 mb-1">Email Address</p>
                                        {teamMember.email ? (
                                            <a href={`mailto:${teamMember.email}`} className="text-blue-600 hover:text-blue-800 font-medium transition-colors">
                                                {teamMember.email}
                                            </a>
                                        ) : (
                                            <span className="text-gray-400">Not provided</span>
                                        )}
                                    </div>
                                </div>

                                <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                                    <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                                        <PhoneIcon size={18} className="text-gray-600" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-sm text-gray-500 mb-1">Phone Number</p>
                                        {teamMember.phone ? (
                                            <a href={`tel:${teamMember.phone}`} className="text-blue-600 hover:text-blue-800 font-medium transition-colors">
                                                {teamMember.phone}
                                            </a>
                                        ) : (
                                            <span className="text-gray-400">Not provided</span>
                                        )}
                                    </div>
                                </div>

                                <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                                    <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                                        <IdCardIcon size={18} className="text-gray-600" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-sm text-gray-500 mb-1">CNIC</p>
                                        <span className="text-gray-900 font-medium">
                                            {teamMember.cnic || "Not provided"}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Account Information */}
                        <div>
                            <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
                                <ShieldIcon size={20} className="text-gray-600" />
                                Account Information
                            </h2>
                            <div className="space-y-4">
                                <div className="p-4 bg-gray-50 rounded-xl">
                                    <div className="flex items-center justify-between mb-2">
                                        <p className="text-sm text-gray-500">User ID</p>
                                        <button
                                            onClick={() => copyToClipboard(teamMember._id)}
                                            className="p-1 hover:bg-gray-200 rounded transition-colors"
                                        >
                                            <CopyIcon size={14} className="text-gray-500" />
                                        </button>
                                    </div>
                                    <p className="font-mono text-sm text-gray-900 break-all">{teamMember._id || "N/A"}</p>
                                </div>

                                <div className="p-4 bg-gray-50 rounded-xl">
                                    <div className="flex items-center justify-between mb-2">
                                        <p className="text-sm text-gray-500">Auth UID</p>
                                        <button
                                            onClick={() => copyToClipboard(teamMember.uid)}
                                            className="p-1 hover:bg-gray-200 rounded transition-colors"
                                        >
                                            <CopyIcon size={14} className="text-gray-500" />
                                        </button>
                                    </div>
                                    <p className="font-mono text-sm text-gray-900 break-all">{teamMember.uid || "N/A"}</p>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="p-4 bg-gray-50 rounded-xl">
                                        <div className="flex items-center gap-2 mb-2">
                                            <CalendarIcon size={14} className="text-gray-500" />
                                            <p className="text-sm text-gray-500">Created</p>
                                        </div>
                                        <p className="text-sm font-medium text-gray-900">
                                            {teamMember.createdDate ? moment(teamMember.createdDate).format("MMM DD, YYYY") : "N/A"}
                                        </p>
                                    </div>

                                    <div className="p-4 bg-gray-50 rounded-xl">
                                        <div className="flex items-center gap-2 mb-2">
                                            <ClockIcon size={14} className="text-gray-500" />
                                            <p className="text-sm text-gray-500">Last Login</p>
                                        </div>
                                        <p className="text-sm font-medium text-gray-900">
                                            {teamMember.lastLogin ? moment(teamMember.lastLogin).fromNow() : "Never"}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Meeting Room Quota */}
                    {/* <div className="border-t pt-8">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                                <ClockIcon size={20} className="text-gray-600" />
                                Meeting Room Quota
                            </h2>
                            {!isEditingMeetingQuota ? (
                                <button
                                    onClick={() => setIsEditingMeetingQuota(true)}
                                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                    <PencilIcon size={18} className="text-gray-600" />
                                </button>
                            ) : (
                                <div className="flex gap-2">
                                    <button
                                        onClick={handleSaveMeetingQuota}
                                        disabled={updateUserMeetingRoomQuotaMutation.isLoading}
                                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                                    >
                                        <SaveIcon size={16} />
                                        Save
                                    </button>
                                    <button
                                        onClick={handleCancelEditMeetingQuota}
                                        disabled={updateUserMeetingRoomQuotaMutation.isLoading}
                                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 flex items-center gap-2"
                                    >
                                        <XIcon size={16} />
                                        Cancel
                                    </button>
                                </div>
                            )}
                        </div>

                        {!isEditingMeetingQuota ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-6 rounded-2xl">
                                    <p className="text-blue-100 mb-2">Monthly Hours</p>
                                    <p className="text-3xl font-bold">{meetingRoomQuota?.monthlyHours || 0}</p>
                                    <p className="text-blue-100 text-sm">hours per month</p>
                                </div>
                                <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-6 rounded-2xl">
                                    <p className="text-green-100 mb-2">Daily Limit</p>
                                    <p className="text-3xl font-bold">{meetingRoomQuota?.meetingRoomHoursPerDay || 0}</p>
                                    <p className="text-green-100 text-sm">hours per day</p>
                                </div>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Monthly Hours ({meetingRoomQuota.monthlyHours} hours)
                                    </label>
                                    <input
                                        type="number"
                                        className="w-full p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                        value={meetingRoomQuota.monthlyHours}
                                        max={744}
                                        min={0}
                                        step={1}
                                        onChange={(e) => setMeetingRoomQuota({
                                            ...meetingRoomQuota,
                                            monthlyHours: e.target.value
                                        })}
                                        disabled={updateUserMeetingRoomQuotaMutation.isLoading}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Daily Limit ({meetingRoomQuota.meetingRoomHoursPerDay} hours/day)
                                    </label>
                                    <input
                                        type="number"
                                        className="w-full p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                        value={meetingRoomQuota.meetingRoomHoursPerDay}
                                        max={24}
                                        min={0}
                                        step={1}
                                        onChange={(e) => setMeetingRoomQuota({
                                            ...meetingRoomQuota,
                                            meetingRoomHoursPerDay: e.target.value
                                        })}
                                        disabled={updateUserMeetingRoomQuotaMutation.isLoading}
                                    />
                                </div>
                            </div>
                        )}
                    </div> */}
                </div>

                {/* Account Management */}
                {can('FX12-1-1', 'account_management') && (
                    <div className="border rounded-3xl p-8">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-8 flex items-center gap-2">
                            <KeyIcon size={24} className="text-gray-600" />
                            Account Management
                        </h2>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            <div className="bg-gray-50 rounded-2xl p-6">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center">
                                        <ShieldIcon size={24} className="text-gray-600" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900">Account Status</h3>
                                        <p className="text-sm text-gray-600">Control login access</p>
                                    </div>
                                    <div className="ml-auto">
                                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${teamMember.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                            {teamMember.isActive ? 'Active' : 'Inactive'}
                                        </span>
                                    </div>
                                </div>
                                <Button
                                    variant="gradient"
                                    color={teamMember.isActive ? "red" : "green"}
                                    size="lg"
                                    className="w-full rounded-xl"
                                    loading={updateUserStatusMutation.isLoading}
                                    onClick={handleUpdateUserStatus}
                                >
                                    {teamMember.isActive ? "Disable Account" : "Enable Account"}
                                </Button>
                            </div>

                            <div className="bg-gray-50 rounded-2xl p-6">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center">
                                        <KeyIcon size={24} className="text-gray-600" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900">Password Reset</h3>
                                        <p className="text-sm text-gray-600">Send reset email to {teamMember.email}</p>
                                    </div>
                                </div>
                                <Button
                                    variant="gradient"
                                    color="blue"
                                    size="lg"
                                    className="w-full rounded-xl"
                                    loading={sendResetPasswordEmailMutation.isLoading}
                                    onClick={handleSendResetPasswordEmail}
                                    disabled={!teamMember.email}
                                >
                                    Send Reset Email
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <BottomUXSpace />
            <ConfirmationDialog />
        </div>
    );
};

export default TeamMemberDetailsPage;